'use client';

/**
 * ArtifactCard — the in-flow presentation of a `data-artifact` part.
 *
 * Two shapes, chosen by `artifactInlinePreview` (the policy lives there, pure
 * and unit-tested):
 *
 * - COLLAPSED row (report, code file, HTML page, big table): kind icon, title,
 *   chip, size, and an "Open" affordance that launches the full ArtifactViewer.
 *   Their content is long, so putting it in the flow would bury the
 *   conversation.
 *
 * - INLINE content (image, chart, table): the thing itself, with the same row
 *   underneath as caption + open affordance. For these the content IS the answer
 *   and fits at a glance — making someone click to find out whether the crop is
 *   right, or what the chart says, is the whole complaint. A long table shows its
 *   first rows and offers "show all N rows", which opens the viewer's full table
 *   (sortable, filterable, exportable).
 */

import React, { useState } from 'react';
import {
  BarChart3Icon,
  Code2Icon,
  FileTextIcon,
  GlobeIcon,
  ImageIcon,
  Maximize2Icon,
  Table2Icon,
} from 'lucide-react';
import type { ArtifactData, ArtifactKind } from '../../lib/artifacts/types';
import { artifactInlinePreview, artifactKindLabel, artifactSizeLabel } from './artifact-utils';
import { ArtifactInlineContent } from './ArtifactRenderers';
import { ArtifactViewer } from './ArtifactViewer';

const KIND_ICONS: Record<ArtifactKind, React.ComponentType<{ className?: string }>> = {
  markdown: FileTextIcon,
  code: Code2Icon,
  html: GlobeIcon,
  table: Table2Icon,
  chart: BarChart3Icon,
  image: ImageIcon,
};

export interface ArtifactCardProps {
  artifact: ArtifactData;
}

export function ArtifactCard({ artifact }: ArtifactCardProps) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const KindIcon = KIND_ICONS[artifact.kind];
  const inlineKind = artifactInlinePreview(artifact);

  return (
    <>
      <div
        className="mt-3 rounded-lg border border-current/20 overflow-hidden"
        data-testid="artifact-card"
      >
        {inlineKind && (
          <div className="p-3 pb-0" data-testid={`artifact-inline-${inlineKind}`}>
            {/* An image is the one kind where clicking the preview should zoom;
                a chart or table is already fully legible, and wrapping a real
                <table> in a <button> would break its semantics for a screen
                reader. Those get the row below as the open affordance. */}
            {inlineKind === 'image' ? (
              <button
                type="button"
                onClick={() => setViewerOpen(true)}
                className="block w-full text-left"
                aria-label={`View full size: ${artifact.title}`}
              >
                <ArtifactInlineContent artifact={artifact} />
              </button>
            ) : (
              <ArtifactInlineContent
                artifact={artifact}
                onExpand={() => setViewerOpen(true)}
              />
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setViewerOpen(true)}
          className="w-full min-h-11 flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
          aria-label={`Open artifact: ${artifact.title}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <KindIcon className="size-4 flex-shrink-0 text-muted-foreground" aria-hidden />
            <span className="font-medium text-sm truncate">{artifact.title}</span>
            <span className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-xs whitespace-nowrap flex-shrink-0">
              {artifactKindLabel(artifact)}
            </span>
            {artifact.kind === 'image' && artifact.image && (
              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {artifact.image.width}×{artifact.image.height}
              </span>
            )}
            <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
              {artifactSizeLabel(artifact)}
            </span>
          </div>
          <span className="flex items-center gap-1 text-xs font-medium text-primary flex-shrink-0">
            {inlineKind ? 'Full size' : 'Open'}
            <Maximize2Icon className="size-3.5" aria-hidden />
          </span>
        </button>
      </div>
      <ArtifactViewer artifact={artifact} open={viewerOpen} onOpenChange={setViewerOpen} />
    </>
  );
}
